import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import { SparklesIcon } from '@heroicons/react/24/outline';
import { BoltIcon, StarIcon } from '@heroicons/react/20/solid';

// Redux
import { setOrderDataAction } from '../../../redux/Actions/CreateOrderActions';
import { getDeliveryTimeApi, getPickupTimeApi } from '../../../redux/Reducers/CreateOrderReducer';
import { getAllProvidersApi } from '../../../redux/Reducers/ProvidersReducer';

// Utils
import { amountCheckFn, formatAndSortStartTimes, timeIdGenarate } from '../../../utils/commonUtil';
import { generateInitials } from '../../../components/Utils';

// Components
import { Section, DateStrip, Placeholder, LoadingRow, Spinner } from './ui';
import { SECTION_IDS, apiType, shortSlot, hasDropoff, hasPickup, isOk, pickupDateOf, toScheduleList } from './helpers';

const NO_RUSH = { is_rush: false };

function ProviderAvatar({ provider, auto }) {
  if (auto)
    return (
      <span className='bg-client-iconbg flex h-7 w-7 shrink-0 items-center justify-center rounded-full'>
        <SparklesIcon className='text-client-300 h-4 w-4' />
      </span>
    );
  if (provider?.profile_image?.absolute_url)
    return (
      <img className='h-7 w-7 shrink-0 rounded-full object-cover' src={provider.profile_image.absolute_url} alt='' />
    );
  return (
    <span className='flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-pink-400 text-[10px] font-bold text-white'>
      {generateInitials(`${provider?.first_name} ${provider?.last_name}`)}
    </span>
  );
}

function ProviderPill({ provider, auto, available, selected, onSelect }) {
  return (
    <button
      type='button'
      disabled={!available}
      onClick={onSelect}
      aria-pressed={selected}
      title={!available ? 'No openings' : provider?.isPreferred ? 'Your preferred provider' : undefined}
      className={`flex items-center gap-2 rounded-md border py-1 pl-1.5 pr-3.5 text-sm font-medium transition ${
        selected
          ? 'border-client-300 bg-client-300/10 text-client-text'
          : 'border-slate-200 text-slate-700 hover:border-slate-300'
      } ${!available ? 'cursor-not-allowed opacity-40' : ''}`}
    >
      <ProviderAvatar provider={provider} auto={auto} />
      {auto ? 'Auto-match' : `${provider.first_name} ${provider.last_name?.charAt(0) || ''}.`}
      {provider?.isPreferred && <StarIcon className='h-3.5 w-3.5 text-amber-400' />}
    </button>
  );
}

function TimePill({ selected, onClick, children }) {
  return (
    <button
      type='button'
      onClick={onClick}
      aria-pressed={selected}
      className={`flex w-full flex-wrap items-center justify-center gap-x-1 whitespace-nowrap rounded-md border px-2 py-2 text-[13px] font-semibold transition ${
        selected
          ? 'border-client-300 bg-client-300 text-white'
          : 'border-slate-200 text-slate-700 hover:border-slate-300'
      }`}
    >
      {children}
    </button>
  );
}

function Row({ label, children }) {
  return (
    <div className='min-w-0'>
      <p className='mb-2 text-sm font-medium text-slate-500'>{label}</p>
      {children}
    </div>
  );
}

const Muted = ({ children }) => <p className='py-1.5 text-sm text-slate-400'>{children}</p>;

export default function ScheduleSection({ refreshKey }) {
  const dispatch = useDispatch();
  const { orderType, orderServiceAddress, selectedServices, orderProvider, orderTimeSlots } = useSelector(
    (s) => s.createNewOrder
  );
  const preferredId = useSelector((s) => s.myaccount.orderPreferences?.preferred_worker?.id);

  const [providers, setProviders] = useState([]);
  const [autoSchedules, setAutoSchedules] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(true);
  const [pickupView, setPickupView] = useState(null);
  const [delivery, setDelivery] = useState([]);
  const [loadingDelivery, setLoadingDelivery] = useState(false);
  const [deliveryView, setDeliveryView] = useState(null);
  const lastDropoffRef = useRef(null);

  const tz = orderServiceAddress?.time_zone;
  const addressId = orderServiceAddress?.id;
  const serviceIdsKey = (selectedServices || []).map((s) => s.service_id).join(',');
  const itemsKey = (selectedServices || []).map((s) => `${s.service_id}:${s.quantity}`).join(',');
  const ready = !!addressId && !!serviceIdsKey;
  const selectedId = orderProvider?.id || null;
  const pickupDate = pickupDateOf(orderTimeSlots);
  const pickupStart = orderTimeSlots?.pickup_start_time;

  // A different address means different providers; don't show the old ones while loading.
  useEffect(() => {
    setProviders([]);
    setAutoSchedules([]);
  }, [addressId]);

  // ── Providers + auto-match availability ─────────────────────────────
  useEffect(() => {
    if (!ready) return;
    let stale = false;
    setLoadingProviders(true);
    const timer = setTimeout(() => {
      const type = apiType(orderType);
      Promise.all([
        getAllProvidersApi(addressId, { service_ids: (selectedServices || []).map((s) => s.service_id) }, type),
        getPickupTimeApi(null, addressId, type),
      ]).then(([provRes, timeRes]) => {
        if (stale) return;
        setLoadingProviders(false);
        const list = (isOk(provRes) ? provRes?.data?.data || [] : [])
          .map((p) => ({
            ...p,
            schedules: toScheduleList(formatAndSortStartTimes(p?.schedules || {}, tz), tz),
            isPreferred: !!preferredId && p?.id === preferredId,
          }))
          .sort((a, b) => (b.isPreferred ? 1 : 0) - (a.isPreferred ? 1 : 0));
        const auto = isOk(timeRes) ? toScheduleList(formatAndSortStartTimes(timeRes?.data || {}, tz), tz) : [];
        setProviders(list);
        setAutoSchedules(auto);

        // Drop a provider or pickup time that is no longer offered.
        dispatch((d, getState) => {
          const { orderProvider: prov, orderTimeSlots: slots } = getState().createNewOrder;
          const current = prov?.id ? list.find((p) => p.id === prov.id) : null;
          if (prov?.id && !current) {
            d(setOrderDataAction({ orderProvider: null, orderTimeSlots: null, orderIsRush: NO_RUSH }));
            return;
          }
          const date = pickupDateOf(slots);
          const sched = current ? current.schedules : auto;
          if (date && !sched.find((s) => s.key === date)?.times.some((t) => t.start === slots.pickup_start_time))
            d(setOrderDataAction({ orderTimeSlots: null, orderIsRush: NO_RUSH }));
        });
      });
    }, 300);
    return () => {
      stale = true;
      clearTimeout(timer);
    };
  }, [addressId, serviceIdsKey, orderType, refreshKey]);

  // ── Delivery times for the chosen pickup ────────────────────────────
  useEffect(() => {
    if (!ready || !pickupDate || !pickupStart) {
      setDelivery([]);
      setLoadingDelivery(false);
      return;
    }
    let stale = false;
    setLoadingDelivery(true);
    const timer = setTimeout(() => {
      const payload = {
        items: (selectedServices || []).map((i) => ({ service_id: i.service_id, quantity: i.quantity })),
        pickup_date: pickupDate,
        pickup_start_time: pickupStart,
        type: orderType === 'oneTime' ? 'ORDER' : 'SUBSCRIPTION',
      };
      getDeliveryTimeApi(selectedId || 'null', addressId, payload, apiType(orderType))
        .then((res) => {
          if (stale) return;
          setLoadingDelivery(false);
          if (!isOk(res)) {
            setDelivery([]);
            toast.error(res?.message || 'Could not load delivery times');
            return;
          }
          const list = toScheduleList(formatAndSortStartTimes(res?.data?.data || {}, tz), tz);
          setDelivery(list);

          // Keep the user's delivery choice if it is still offered; otherwise default to the first standard slot.
          dispatch((d, getState) => {
            const slots = getState().createNewOrder.orderTimeSlots;
            if (!slots || pickupDateOf(slots) !== pickupDate || slots.pickup_start_time !== pickupStart) return;
            const all = list.flatMap((day) => day.times.map((t) => ({ date: day.key, ...t })));
            const want = slots.dropoff_date
              ? { date: slots.dropoff_date, start: slots.dropoff_start_time }
              : lastDropoffRef.current;
            const pick =
              (want && all.find((x) => x.date === want.date && x.start === want.start)) ||
              all.find((x) => !x.is_rush) ||
              all[0];
            if (pick) applyDropoff(d, slots, pick);
          });
        })
        .catch(() => !stale && setLoadingDelivery(false));
    }, 250);
    return () => {
      stale = true;
      clearTimeout(timer);
    };
  }, [ready, pickupDate, pickupStart, selectedId, addressId, itemsKey, orderType]);

  const applyDropoff = (d, slots, pick) => {
    lastDropoffRef.current = { date: pick.date, start: pick.start };
    d(
      setOrderDataAction({
        orderTimeSlots: {
          ...slots,
          dropoff_date: pick.date,
          dropoff_start_time: pick.start,
          dropoff_end_time: pick.end,
          selected_dropoff_time_id: timeIdGenarate(pick.date, pick.start),
        },
        orderIsRush: pick.raw,
      })
    );
  };

  // ── Actions ─────────────────────────────────────────────────────────
  const schedulesFor = (id) => (id ? providers.find((p) => p.id === id)?.schedules || [] : autoSchedules);

  const chooseProvider = (p) => {
    const id = p?.id || null;
    if (id === selectedId) return;
    // Keep the pickup time if the new provider offers the same slot.
    const keepSlot =
      pickupDate &&
      schedulesFor(id)
        .find((s) => s.key === pickupDate)
        ?.times.some((t) => t.start === pickupStart);
    dispatch(
      setOrderDataAction({
        orderProvider: id
          ? { id, first_name: p.first_name, last_name: p.last_name, profile_image: p.profile_image }
          : { id: null, first_name: 'Auto Match' },
        ...(!keepSlot && { orderTimeSlots: null, orderIsRush: NO_RUSH }),
      })
    );
    if (!keepSlot) setPickupView(null);
  };

  const choosePickup = (date, t) => {
    if (date === pickupDate && t.start === pickupStart) return;
    dispatch(
      setOrderDataAction({
        orderTimeSlots: {
          ...(orderType === 'oneTime' ? { pickup_date: date } : { first_pickup_date: date }),
          pickup_start_time: t.start,
          pickup_end_time: t.end,
          timeZone: t.tzAbbr,
        },
        orderIsRush: NO_RUSH,
      })
    );
  };

  const chooseDropoff = (date, t) => applyDropoff(dispatch, orderTimeSlots, { date, ...t });

  // ── Derived view state ──────────────────────────────────────────────
  const pickupSchedules = schedulesFor(selectedId);
  const has = (list, key) => key && list.some((s) => s.key === key);
  const activePickupDate = has(pickupSchedules, pickupView)
    ? pickupView
    : has(pickupSchedules, pickupDate)
    ? pickupDate
    : pickupSchedules[0]?.key;
  const activeDeliveryDate = has(delivery, deliveryView)
    ? deliveryView
    : has(delivery, orderTimeSlots?.dropoff_date)
    ? orderTimeSlots.dropoff_date
    : delivery[0]?.key;
  const pickupTimes = pickupSchedules.find((s) => s.key === activePickupDate)?.times || [];
  const deliveryTimes = delivery.find((s) => s.key === activeDeliveryDate)?.times || [];
  const tzAbbr = pickupTimes[0]?.tzAbbr || autoSchedules[0]?.times?.[0]?.tzAbbr;
  const anyRush = delivery.some((day) => day.times.some((t) => t.is_rush));
  const isSub = orderType === 'subscription';

  // Default to the earliest pickup slot for the selected provider.
  useEffect(() => {
    if (!ready || loadingProviders || hasPickup(orderTimeSlots)) return;
    const first = pickupSchedules[0];
    if (first?.times?.[0]) choosePickup(first.key, first.times[0]);
  }, [ready, loadingProviders, selectedId, pickupSchedules.length, orderTimeSlots]);

  return (
    <Section
      id={SECTION_IDS.schedule}
      step={3}
      title='Pickup & delivery'
      subtitle={ready && tzAbbr ? `Times shown in ${tzAbbr}` : null}
      summary={
        hasPickup(orderTimeSlots)
          ? `${shortSlot(pickupDate, pickupStart, orderTimeSlots.pickup_end_time, tz)}${
              hasDropoff(orderTimeSlots)
                ? ` → ${shortSlot(
                    orderTimeSlots.dropoff_date,
                    orderTimeSlots.dropoff_start_time,
                    orderTimeSlots.dropoff_end_time,
                    tz
                  )}`
                : ''
            }`
          : null
      }
      done={hasPickup(orderTimeSlots) && hasDropoff(orderTimeSlots)}
      disabled={!ready}
    >
      {!ready ? (
        <Placeholder>Add a service to see available pickup times.</Placeholder>
      ) : loadingProviders && !providers.length && !autoSchedules.length ? (
        <LoadingRow text='Finding available providers…' />
      ) : (
        <div className={`space-y-5 transition-opacity ${loadingProviders ? 'pointer-events-none opacity-50' : ''}`}>
          <Row label='Provider'>
            <div className='flex flex-wrap gap-2'>
              <ProviderPill
                auto
                available={autoSchedules.length > 0}
                selected={!selectedId}
                onSelect={() => chooseProvider(null)}
              />
              {providers.map((p) => (
                <ProviderPill
                  key={p.id}
                  provider={p}
                  available={p.schedules.length > 0}
                  selected={selectedId === p.id}
                  onSelect={() => chooseProvider(p)}
                />
              ))}
            </div>
          </Row>

          <div className='grid gap-5 border-t border-slate-100 pt-5 md:grid-cols-2 md:gap-8'>
            <Row label={isSub ? 'First pickup' : 'Pickup'}>
              {pickupSchedules.length === 0 ? (
                <Muted>No openings for this provider. Try Auto-match.</Muted>
              ) : (
                <>
                  <DateStrip dates={pickupSchedules} value={activePickupDate} onChange={setPickupView} />
                  <div className='mt-2 grid grid-cols-2 gap-2'>
                    {pickupTimes.map((t) => (
                      <TimePill
                        key={t.start}
                        selected={pickupDate === activePickupDate && pickupStart === t.start}
                        onClick={() => choosePickup(activePickupDate, t)}
                      >
                        {t.label}
                      </TimePill>
                    ))}
                  </div>
                </>
              )}
            </Row>

            <Row label='Delivery'>
              {!hasPickup(orderTimeSlots) ? (
                <Muted>Choose a pickup time first.</Muted>
              ) : loadingDelivery ? (
                <p className='flex items-center gap-2 py-1.5 text-sm text-slate-400'>
                  <Spinner /> Loading delivery times…
                </p>
              ) : delivery.length === 0 ? (
                <Muted>No delivery times for this pickup. Try another pickup time.</Muted>
              ) : (
                <>
                  <DateStrip dates={delivery} value={activeDeliveryDate} onChange={setDeliveryView} />
                  <div className='mt-2 grid grid-cols-2 gap-2'>
                    {deliveryTimes.map((t) => {
                      const selected =
                        orderTimeSlots?.dropoff_date === activeDeliveryDate &&
                        orderTimeSlots?.dropoff_start_time === t.start;
                      return (
                        <TimePill
                          key={t.start}
                          selected={selected}
                          onClick={() => chooseDropoff(activeDeliveryDate, t)}
                        >
                          {t.label}
                          {t.is_rush && (
                            <span
                              className={`flex items-center text-xs font-semibold ${
                                selected ? 'text-white' : 'text-amber-600'
                              }`}
                            >
                              <BoltIcon className='h-3 w-3' />+{amountCheckFn(t.priority_fee)}
                            </span>
                          )}
                        </TimePill>
                      );
                    })}
                  </div>
                  {anyRush && (
                    <p className='mt-2 flex items-center gap-1 text-xs text-slate-400'>
                      <BoltIcon className='h-3 w-3 text-amber-500' />
                      Express delivery, extra fee
                    </p>
                  )}
                </>
              )}
            </Row>
          </div>
        </div>
      )}
    </Section>
  );
}
